import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    EventEmitter,
    inject,
    input,
    output,
    Output,
    signal,
    TemplateRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
    AncColumnDef,
    AncColumnHideBelow,
    AncGridConfig,
    AncRowClickEvent,
    AncSortEvent
} from './anc-grid.model';
import { AncPaginationComponent } from './anc-pagination/anc-pagination.component';
import { AncCheckboxComponent } from '../anc-checkbox/anc-checkbox.component';
import { AncTooltipDirective } from '../../directives/anc-tooltip/anc-tooltip.directive';
import {
    ANC_GRID_COPYABLE_KEYS,
    ANC_GRID_COPY_RENDER_METHODS,
    ANC_GRID_NO_TOOLTIP_RENDER_METHODS,
    defaultAncCellRenderer,
    type AncCellRendererFn
} from './anc-grid-cell-renderer.util';

type CellRenderer = AncCellRendererFn;

@Component({
    selector: 'lib-anc-grid',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        AncPaginationComponent,
        AncCheckboxComponent,
        AncTooltipDirective
    ],
    templateUrl: './anc-grid.component.html',
    styleUrls: ['./anc-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncGridComponent {
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly toastr = inject(ToastrService);
    private readonly translate = inject(TranslateService);

    columns = input<AncColumnDef[]>([]);
    rows = input<any[]>([]);
    selectedIds = input<string[]>([]);
    loading = input<boolean>(false);
    noData = input<boolean>(false);
    config = input<AncGridConfig>({});
    totalCount = input<number>(0);
    currentPage = input<number>(1);
    pageSize = input<number>(10);
    pageSizeOptions = input<number[]>([10, 20, 50, 100, 200, 500]);
    cellRenderer = input<CellRenderer | null>(null);
    /** When set, the action column is hidden if no loaded row returns true. */
    rowHasActions = input<((row: unknown) => boolean) | null>(null);
    actionTemplate = input<TemplateRef<any> | null>(null);
    cellTemplates = input<Record<string, TemplateRef<any>>>({});
    /** Optional template rendered in a full-width row below the parent when its id is in expandedRowIds. */
    rowDetailTemplate = input<TemplateRef<any> | null>(null);
    /** Row ids that should show rowDetailTemplate (parent-owned state). */
    expandedRowIds = input<readonly string[]>([]);
    /**
     * Optional parent-owned sort state. Prefer omitting this — the grid sorts
     * current-page rows in memory when a column has `sortable: true`.
     */
    activeSort = input<AncSortEvent | null>(null);

    selectionChange = output<string[]>();
    sortChange = output<AncSortEvent>();
    pageChange = output<number>();
    pageSizeChange = output<number>();
    /**
     * Declared as an EventEmitter (not `output()`) on purpose: `observed` lets the grid
     * tell whether a parent actually listens, so a `clickable` column never renders the
     * link colour / pointer affordance when the click would do nothing.
     */
    @Output() rowClick = new EventEmitter<AncRowClickEvent>();

    /** True only when a parent bound `(rowClick)`. Drives the link affordance. */
    private get hasRowClickListener(): boolean {
        return this.rowClick.observed;
    }

    /**
     * Client-side sort state. Prefer this over `activeSort` so rows reorder immediately
     * without waiting for a parent refetch / API call.
     */
    private readonly clientSort = signal<AncSortEvent | null>(null);

    /**
     * Temporary widths locked only while a sort is in progress (reorder / loading).
     * Cleared afterward so layout returns to normal auto sizing.
     */
    readonly frozenColWidths = signal<ReadonlyMap<string, string>>(new Map());
    /** True after sort click until widths are released. */
    private readonly sortWidthLock = signal(false);
    /** True if `loading` became true while the sort lock was held (API refetch path). */
    private readonly sawLoadingWhileLocked = signal(false);

    readonly skeletonRows = Array(8).fill(null);

    /** O(1) membership lookup, re-derived only when selectedIds changes. */
    private readonly selectedIdsSet = computed(
        () => new Set(this.selectedIds())
    );
    private readonly expandedIdsSet = computed(
        () => new Set(this.expandedRowIds())
    );

    readonly resolvedSort = computed(
        () => this.clientSort() ?? this.activeSort()
    );

    readonly hasFrozenWidths = computed(() => this.frozenColWidths().size > 0);

    /** Resolved width for th/td — frozen only during an active sort; otherwise author width. */
    getColumnWidth(col: AncColumnDef): string | null {
        return this.frozenColWidths().get(col.key) ?? col.width ?? null;
    }

    /** Snapshot header widths at sort start so skeleton/reorder cannot reflow columns. */
    private freezeWidthsForSort(): void {
        if (this.frozenColWidths().size > 0) {
            return;
        }
        const cols = this.displayColumns();
        if (!cols.length) {
            return;
        }

        const ths = Array.from(
            this.host.nativeElement.querySelectorAll(
                'thead tr.anc-thead-row > th.anc-th:not(.anc-th-check)'
            )
        ) as HTMLElement[];
        if (!ths.length || ths.length !== cols.length) {
            return;
        }

        const next = new Map<string, string>();
        ths.forEach((th, index) => {
            const col = cols[index];
            if (!col) {
                return;
            }
            if (col.width) {
                next.set(col.key, col.width);
                return;
            }
            const width = th.getBoundingClientRect().width;
            if (width > 0) {
                next.set(col.key, `${Math.round(width)}px`);
            }
        });

        if (next.size > 0) {
            this.frozenColWidths.set(next);
        }
    }

    private releaseSortWidths(): void {
        if (!this.sortWidthLock() && this.frozenColWidths().size === 0) {
            return;
        }
        this.sortWidthLock.set(false);
        this.sawLoadingWhileLocked.set(false);
        this.frozenColWidths.set(new Map());
        this.cdr.markForCheck();
    }

    /**
     * Release frozen widths after a sort-triggered refetch finishes.
     * Watches only `loading` / lock flags — not columns — to avoid CD loops.
     */
    private readonly releaseWidthsWhenSortSettles = effect(
        () => {
            const loading = this.loading();
            if (!this.sortWidthLock()) {
                return;
            }
            if (loading) {
                this.sawLoadingWhileLocked.set(true);
                return;
            }
            if (!this.sawLoadingWhileLocked()) {
                return;
            }
            queueMicrotask(() => {
                if (this.sortWidthLock() && !this.loading()) {
                    this.releaseSortWidths();
                }
            });
        },
        { allowSignalWrites: true }
    );

    /** Current page rows in backend order, or sorted when a column sort is active. */
    readonly sortedRows = computed(() => {
        const rows = this.rows();
        const sort = this.resolvedSort();
        if (!sort?.key || !rows.length) {
            return rows;
        }
        const col = this.columns().find(
            (c) => (c.sortKey || c.key) === sort.key || c.key === sort.key
        );
        const factor = sort.direction === 'desc' ? -1 : 1;
        return [...rows].sort((left, right) => {
            const cmp = this.compareSortValues(
                this.getSortRawValue(left, sort.key, col),
                this.getSortRawValue(right, sort.key, col)
            );
            return factor * cmp;
        });
    });

    readonly displayColumns = computed(() => {
        const cols = this.columns();
        const rowHasActions = this.rowHasActions();
        if (!rowHasActions || this.loading()) {
            return cols;
        }
        const rows = this.rows();
        if (!rows.length) {
            return cols;
        }
        if (rows.some((row) => rowHasActions(row))) {
            return cols;
        }
        return cols.filter((col) => col.cellType !== 'action');
    });

    /** Match legacy ngx-grid: show footer whenever the grid is not in empty state. */
    readonly showPagination = computed(() => {
        if (this.noData()) return false;
        return this.rows().length > 0 || (this.totalCount() ?? 0) > 0;
    });

    readonly allSelected = computed(() => {
        const rows = this.rows();
        const set = this.selectedIdsSet();
        return rows.length > 0 && rows.every((r) => set.has(r.id));
    });

    readonly someSelected = computed(
        () => this.selectedIdsSet().size > 0 && !this.allSelected()
    );

    /** Staggered widths so adjacent shimmer rows look like real, varied content. */
    private readonly shimmerWidths = [
        '72%',
        '55%',
        '85%',
        '48%',
        '68%',
        '80%',
        '42%',
        '62%'
    ];
    private readonly shimmerSecondary = [
        '40%',
        '55%',
        '35%',
        '50%',
        '30%',
        '45%',
        '38%',
        '52%'
    ];

    shimmerPrimaryWidth(rowIdx: number, colIdx: number): string {
        return this.shimmerWidths[
            (rowIdx * 3 + colIdx) % this.shimmerWidths.length
        ];
    }

    shimmerSecondaryWidth(rowIdx: number, colIdx: number): string {
        return this.shimmerSecondary[
            (rowIdx * 5 + colIdx + 2) % this.shimmerSecondary.length
        ];
    }

    isSelected(row: any): boolean {
        return this.selectedIdsSet().has(row?.id);
    }

    toggleRow(row: any, checked: boolean): void {
        const ids = this.selectedIds();
        const next = checked
            ? [...ids, row.id]
            : ids.filter((id) => id !== row.id);
        this.selectionChange.emit(next);
    }

    toggleAllRows(checked: boolean): void {
        this.selectionChange.emit(checked ? this.rows().map((r) => r.id) : []);
    }

    /**
     * Toggle sort for a column (unsorted/other → asc → desc → asc).
     * Reorders the current page in-memory; does not call any API.
     * Parents only need `sortable: true` on the column — no sortChange handler required.
     */
    onSortClick(col: AncColumnDef): void {
        if (!col.sortable || this.loading()) return;
        this.freezeWidthsForSort();
        this.sortWidthLock.set(true);
        this.sawLoadingWhileLocked.set(false);
        const key = col.sortKey || col.key;
        const current = this.resolvedSort();
        const direction =
            current?.key === key && current.direction === 'asc'
                ? 'desc'
                : 'asc';
        const next: AncSortEvent = { key, direction };
        this.clientSort.set(next);
        this.sortChange.emit(next);
        this.cdr.markForCheck();
        // Client-only sort: release after a tick if parent never sets loading.
        // API sort: effect releases when loading finishes.
        setTimeout(() => {
            if (!this.sortWidthLock()) return;
            if (this.loading() || this.sawLoadingWhileLocked()) return;
            this.releaseSortWidths();
        }, 0);
    }

    onSortHeaderKeydown(event: KeyboardEvent, col: AncColumnDef): void {
        event.preventDefault();
        this.onSortClick(col);
    }

    getSortDirection(col: AncColumnDef): 'asc' | 'desc' | null {
        const key = col.sortKey || col.key;
        const current = this.resolvedSort();
        return current?.key === key ? current.direction : null;
    }

    getAriaSort(col: AncColumnDef): string | null {
        if (!col.sortable) return null;
        const dir = this.getSortDirection(col);
        if (dir === 'asc') return 'ascending';
        if (dir === 'desc') return 'descending';
        return 'none';
    }

    private getSortRawValue(
        row: any,
        key: string,
        col?: AncColumnDef
    ): unknown {
        const direct =
            row?.[key] ??
            row?.attributes?.[key] ??
            row?.attributes?.product_stocks?.[key] ??
            null;
        if (direct != null && direct !== '') {
            return direct;
        }
        // Fall back to rendered cell text so component/renderMethod columns still sort.
        if (col) {
            const rendered = this.getCellValue(row, col);
            if (rendered != null && rendered !== '' && rendered !== '-') {
                return String(rendered)
                    .replace(/<[^>]*>/g, '')
                    .trim();
            }
        }
        return null;
    }

    private normalizeSortValue(value: unknown): string | number | null {
        if (value == null || value === '') {
            return null;
        }
        if (typeof value === 'number') {
            // IDs often arrive as numbers — keep as string for character-first sort.
            return Number.isFinite(value) ? String(value) : null;
        }
        if (typeof value === 'boolean') {
            return value ? 1 : 0;
        }
        if (Array.isArray(value)) {
            const joined = value
                .map((item) => {
                    if (item == null) return '';
                    if (typeof item === 'object') {
                        const obj = item as Record<string, unknown>;
                        return String(
                            obj['id'] ??
                                obj['code'] ??
                                obj['sku'] ??
                                obj['name'] ??
                                obj['label'] ??
                                obj['value'] ??
                                ''
                        );
                    }
                    return String(item);
                })
                .filter((part) => part !== '')
                .join(', ');
            return joined === '' ? null : joined;
        }
        if (typeof value === 'object') {
            const obj = value as Record<string, unknown>;
            const nested =
                obj['id'] ??
                obj['code'] ??
                obj['sku'] ??
                obj['name'] ??
                obj['label'] ??
                obj['value'];
            if (nested != null && typeof nested !== 'object') {
                return this.normalizeSortValue(nested);
            }
            return JSON.stringify(value);
        }
        const text = String(value).trim();
        if (!text) {
            return null;
        }
        // Digit-only IDs stay strings (character-first). ISO dates also sort as text.
        return text.toLowerCase();
    }

    private compareSortValues(left: unknown, right: unknown): number {
        const a = this.normalizeSortValue(left);
        const b = this.normalizeSortValue(right);
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;

        // Characters (letters) first, then numbers — not length / numeric magnitude.
        const bucketA = this.getSortBucket(a);
        const bucketB = this.getSortBucket(b);
        if (bucketA !== bucketB) {
            return bucketA - bucketB;
        }

        return this.compareAlphaThenNumeric(String(a), String(b));
    }

    /** 0 = starts with a letter, 1 = starts with a digit / is numeric, 2 = other. */
    private getSortBucket(value: string | number): number {
        if (typeof value === 'number') {
            return 1;
        }
        const first = value.trim().charAt(0);
        if (!first) {
            return 2;
        }
        if (/[a-z]/i.test(first)) {
            return 0;
        }
        if (/[0-9]/.test(first)) {
            return 1;
        }
        return 2;
    }

    /**
     * Compare strings chunk-by-chunk: letter runs before digit runs at the same
     * position; within letter runs use character order; within digit runs use
     * character order (not numeric magnitude).
     */
    private compareAlphaThenNumeric(left: string, right: string): number {
        const leftParts = left.match(/([a-z]+)|([0-9]+)|([^a-z0-9]+)/gi) ?? [
            left
        ];
        const rightParts = right.match(/([a-z]+)|([0-9]+)|([^a-z0-9]+)/gi) ?? [
            right
        ];
        const len = Math.max(leftParts.length, rightParts.length);

        for (let i = 0; i < len; i++) {
            const l = leftParts[i] ?? '';
            const r = rightParts[i] ?? '';
            if (l === r) {
                continue;
            }

            const lIsAlpha = /^[a-z]+$/i.test(l);
            const rIsAlpha = /^[a-z]+$/i.test(r);
            const lIsNum = /^[0-9]+$/.test(l);
            const rIsNum = /^[0-9]+$/.test(r);

            // Letter chunk before digit chunk when types differ.
            if (lIsAlpha && rIsNum) return -1;
            if (lIsNum && rIsAlpha) return 1;

            if (lIsNum && rIsNum) {
                // Character-first on digit runs (e.g. "1009…" before "8866…"),
                // not numeric magnitude which effectively sorts by length for IDs.
                const cmp = l.localeCompare(r, undefined, {
                    numeric: false,
                    sensitivity: 'base'
                });
                if (cmp !== 0) return cmp;
                continue;
            }

            const cmp = l.localeCompare(r, undefined, { sensitivity: 'base' });
            if (cmp !== 0) return cmp;
        }

        return 0;
    }

    getCellValue(row: any, col: AncColumnDef): string {
        const rawValue = row[col.key] ?? row.attributes?.[col.key] ?? null;
        if (col.renderMethod) {
            const renderer = this.cellRenderer() ?? defaultAncCellRenderer;
            return renderer(rawValue, col.renderMethod, row) ?? '-';
        }
        return rawValue ?? '-';
    }

    /** Tooltip text for truncated default cells — derived from the rendered value (HTML stripped) so it covers renderMethod columns too. */
    getCellTooltip(row: any, col: AncColumnDef): string | null {
        if (
            col.renderMethod &&
            ANC_GRID_NO_TOOLTIP_RENDER_METHODS.has(col.renderMethod)
        ) {
            return null;
        }
        const rendered = this.getCellValue(row, col);
        if (rendered == null || rendered === '' || rendered === '-') {
            return null;
        }
        const text = String(rendered)
            .replace(/<[^>]*>/g, '')
            .trim();
        return text || null;
    }

    getCellTemplate(col: AncColumnDef): TemplateRef<any> | null {
        return col.componentType
            ? this.cellTemplates()[col.componentType] ?? null
            : null;
    }

    onRowClick(row: any, col: AncColumnDef): void {
        this.rowClick.emit({ item: row, col });
    }

    usesEmbeddedCopyRenderer(col: AncColumnDef): boolean {
        return (
            !!col.renderMethod &&
            ANC_GRID_COPY_RENDER_METHODS.has(col.renderMethod)
        );
    }

    isColumnCopyable(col: AncColumnDef): boolean {
        if (col.copyable === false) return false;
        if (this.usesEmbeddedCopyRenderer(col)) return false;
        if (col.copyable) return true;
        return ANC_GRID_COPYABLE_KEYS.has(col.key);
    }

    getCopyValue(row: any, col: AncColumnDef): string {
        const key = col.copyKey ?? col.key;
        const raw = row?.[key] ?? row?.attributes?.[key];
        if (Array.isArray(raw)) {
            return raw.filter((v) => v != null && v !== '').join(', ');
        }
        return raw != null && raw !== '' ? String(raw) : '';
    }

    hasCopyValue(row: any, col: AncColumnDef): boolean {
        return this.getCopyValue(row, col).length > 0;
    }

    copyToClipboard(value: string, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (!value) return;
        navigator.clipboard
            .writeText(value)
            .then(() => this.showCopyToast())
            .catch(() => {});
    }

    /** Returns true when the click was handled as an embedded copy action. */
    onCellContentClick(event: MouseEvent): boolean {
        const target = event.target as HTMLElement;
        const trigger = target.closest('[data-anc-copy]') as HTMLElement | null;
        if (!trigger) return false;
        const value = trigger.getAttribute('data-anc-copy');
        if (value) this.copyToClipboard(value, event);
        return true;
    }

    onCellTextClick(event: MouseEvent, row: any, col: AncColumnDef): void {
        if (this.onCellContentClick(event)) return;
        if (col.clickable && this.hasRowClickListener)
            this.onRowClick(row, col);
    }

    private showCopyToast(): void {
        this.toastr.info(this.translate.instant('Content Copied'));
    }

    /**
     * Rows with an id keep that id as their key, unchanged. Rows without one
     * fall back to a position-derived key: returning a bare `row?.id` meant
     * every id-less row keyed as `undefined`, and two of them in the same set
     * is a duplicate @for key — NG0955 at runtime.
     */
    trackById = (index: number, row: any): string =>
        row?.id ?? `__row_${index}`;

    isRowExpanded(row: any): boolean {
        return this.expandedIdsSet().has(row?.id);
    }

    detailColspan(): number {
        let count = this.displayColumns().length;
        if (this.config()?.hasCheckbox) {
            count += 1;
        }
        return count;
    }

    getColumnNgClass(
        col: AncColumnDef,
        prefix: 'anc-th' | 'anc-td'
    ): Record<string, boolean> {
        const classes: Record<string, boolean> = {};
        if (prefix === 'anc-th') {
            classes['anc-th-sortable'] = !!col.sortable;
            classes['anc-th-action'] = col.cellType === 'action';
            classes['anc-th-sticky-left'] = !!col.stickyLeft;
            classes['anc-th-sticky-right'] = !!col.stickyRight;
        } else {
            // Only advertise the click affordance when a parent is listening (see rowClick).
            classes['anc-td-link'] =
                !!col.clickable && this.hasRowClickListener;
            classes['anc-td-action'] = col.cellType === 'action';
            classes['anc-td-sticky-left'] = !!col.stickyLeft;
            classes['anc-td-sticky-right'] = !!col.stickyRight;
        }
        const hideBelow = this.effectiveHideBelow(col);
        if (hideBelow) classes[`anc-col-hide-lt-${hideBelow}`] = true;
        if (col.customClass) classes[col.customClass] = true;
        return classes;
    }

    /** A column holding the active sort is never hidden, so the applied sort stays visible. */
    private effectiveHideBelow(col: AncColumnDef): AncColumnHideBelow | null {
        if (!col.hideBelow) return null;
        const sortKey = this.resolvedSort()?.key;
        if (sortKey && (sortKey === (col.sortKey || col.key) || sortKey === col.key)) return null;
        return col.hideBelow;
    }
}
